import { useEffect, useState } from 'react';
import { useFormV2 } from '@abyss/web/hooks/useForm';
import { AlertService } from '@src/api/common/AlertService';
import { AlertVariantType } from '@src/constants/AlertVariantType';
import { ApiToInputDataTransformService } from '@src/api/common/ApiToInputDataTransformService';
import { BreadcrumbService } from '@src/api/common/BreadcrumbService';
import { PageTitleService } from '@src/api/common/PageTitleService';
import { Breadcrumbs, Title } from './PageInfo';
import { UserService } from '@src/api/common/UserService';
import { ImpersonationService } from '@src/api/web-services/features/impersonation';
import { useAuthSessionStore } from '@src/store/common/AuthSessionStore';

type Data = {
  id: string | undefined;
  roles: Array<any>;
};

export const ImpersonateController = () => {
  const [pageData, setPageData] = useState<Data>({
    roles: [],
    id: undefined,
  });

  const { AlertModel, ShowAlert, HideAlert } = AlertService();
  const { GetSelectInputOptions } = ApiToInputDataTransformService();
  const { UpdateBreadcrumb } = BreadcrumbService();
  const { UpdatePageTitle } = PageTitleService();

  const { Impersonate, GetRoles } = ImpersonationService();
  const IsImpersonating = useAuthSessionStore((state) => state.isImpersonating);
  const syncFromCookies = useAuthSessionStore((state) => state.syncFromCookies);

  const form = useFormV2({
    defaultValues: {
      id: undefined,
    },
  });

  const DataSource = {
    GetRoleList: async () => {
      const transformedData = await GetSelectInputOptions(
        'id',
        'name',
        GetRoles
      );
      await setPageData((p) => ({
        ...p,
        roles: transformedData.data,
      }));
    },

    SetImpersonate: async () => {
      const body = form.getValues();
      const data = await Impersonate({
        targetRoleId: body.id,
        reason: '',
      });

      if (!data.data?.hasError && data.data?.result?.accessToken) {
        UserService.SaveImpersonationSwitch();
        UserService.SetUser(data.data.result);
        syncFromCookies({ incrementEpoch: true });
        await HideAlert();
      } else if (data.data?.hasError && data.data?.message) {
        AlertModel.variant = AlertVariantType.ERROR;
        AlertModel.message = data.data?.message;
        AlertModel.visible = true;
        await ShowAlert(AlertModel);
      }
    },
  };

  const Action = {
    Load: async () => {
      await DataSource.GetRoleList();
    },
    Impersonate: async () => {
      await DataSource.SetImpersonate();
    },
    RemoveImpersonation: async () => {
      const restored = UserService.RestoreImpersonationSwitch();
      if (restored) {
        syncFromCookies({ incrementEpoch: true });
      }
    },
    Initialize: async () => {
      await UpdateBreadcrumb(Breadcrumbs);
      await UpdatePageTitle(Title);
      await Action.Load();
    },
  };

  useEffect(() => {
    syncFromCookies();
    Action.Initialize();
  }, []);

  return {
    form,
    Action,
    pageData,
    GetRoles,
    Impersonate,
    IsImpersonating,
  };
};
